import { useMutation } from "@tanstack/react-query";
import { ArrowLeft, Building, Clock, LogOut, ScrollText, Shield, ShieldAlert, type LucideIcon } from "lucide-react";
import { Suspense } from "react";
import { useTranslation } from "react-i18next";
import { Link, Outlet, useLocation, useNavigate } from "react-router";

import { logoutBackend } from "@/api/auth";
import {
  apiClient,
  clearImpersonatingTenantId,
  clearToken,
  getRefreshToken,
  getToken,
  setToken,
} from "@/api/client";
import { ErrorBoundary } from "@/components/ui/ErrorBoundary";
import { SkeletonText } from "@/components/ui/Skeleton";
import { useAuth } from "@/hooks/useAuth";
import { decodeJwtClaims } from "@/lib/jwt";
import { cn } from "@/lib/utils";

interface AdminNavItem {
  to: string;
  labelKey: string;
  icon: LucideIcon;
}

const ADMIN_NAV_ITEMS: AdminNavItem[] = [
  { to: "/admin/tenants", labelKey: "nav.tenants", icon: Building },
  { to: "/admin/jobs", labelKey: "nav.jobs", icon: Clock },
  { to: "/admin/audit", labelKey: "nav.audit", icon: ScrollText },
];

export default function AdminLayout() {
  const { t } = useTranslation();
  const { user, isLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const claims = decodeJwtClaims(getToken());
  const isSuperAdmin = user?.is_super_admin === true || claims?.is_super_admin === true;
  const isImpersonating = claims?.impersonating === true;

  // Super-admin koji je upravo prešao u impersonation ne treba da gleda admin
  // konzolu — vrati ga u tenant aplikaciju da bi sve query-je dobile nov scope.
  const exitImpersonationMut = useMutation({
    mutationFn: async () => {
      // Eksplicitan izlaz: skidamo impersonation memo da auto-restore u
      // runRefresh ne pokuša da nas vrati u impersonation kontekst.
      clearImpersonatingTenantId();
      const refresh = getRefreshToken();
      if (!refresh) throw new Error("No refresh token");
      const { data } = await apiClient.post<{ access_token: string; refresh_token: string }>(
        "/api/auth/refresh",
        { refresh_token: refresh },
      );
      setToken(data.access_token);
      if (data.refresh_token) {
        localStorage.setItem("eslsync_refresh_token", data.refresh_token);
      }
    },
    onSuccess: () => {
      window.location.assign("/admin/tenants");
    },
  });

  async function handleLogout() {
    const refresh = getRefreshToken();
    if (refresh) {
      await logoutBackend(refresh);
    }
    clearToken();
    void navigate("/login", { replace: true });
  }

  // Guard: samo super-admin sme u admin konzolu. Tenant useri dobijaju 403 view.
  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-950">
        <SkeletonText lines={3} className="max-w-sm" />
      </div>
    );
  }
  if (!isSuperAdmin) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4 bg-slate-950 px-4 text-slate-100">
        <ShieldAlert className="h-12 w-12 text-rose-400" />
        <h1 className="text-xl font-semibold">{t("admin_layout.forbidden_title")}</h1>
        <p className="max-w-md text-center text-sm text-slate-400">
          {t("admin_layout.forbidden_hint")}
        </p>
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-700 bg-slate-900 px-3 py-1.5 text-sm hover:bg-slate-800"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          {t("admin_layout.back_to_app")}
        </Link>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col bg-slate-950">
      <header className="flex h-14 shrink-0 items-stretch">
        {/* Brand sekcija — prepoznatljiva master konzola identitet */}
        <div className="flex w-60 shrink-0 items-center gap-2.5 border-b border-purple-900/40 bg-slate-950 px-4">
          <Shield className="h-5 w-5 text-purple-400" />
          <span className="h-5 w-1 shrink-0 rounded-full bg-purple-500" />
          <div className="min-w-0">
            <div className="truncate text-sm font-bold leading-tight tracking-tight text-purple-100">
              {t("admin_layout.brand")}
            </div>
            <div className="truncate text-[10px] leading-tight text-purple-400/60">
              {t("admin_layout.brand_subtitle")}
            </div>
          </div>
        </div>

        {/* Top bar kontrole */}
        <div className="flex flex-1 items-center justify-end gap-2.5 border-b border-purple-900/40 bg-slate-900 px-5">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-700 bg-slate-800 px-2.5 py-1 text-xs font-medium text-slate-200 transition-colors hover:bg-slate-700"
          >
            <ArrowLeft size={11} />
            {t("admin_layout.back_to_app")}
          </Link>
          <span className="hidden font-mono text-xs text-slate-400 sm:block">
            {user?.email}
          </span>
          <span className="inline-flex items-center gap-1 rounded bg-purple-900/40 px-2 py-0.5 text-[10px] font-medium uppercase text-purple-200">
            <Shield size={10} />
            {t("admin_layout.super_admin_badge")}
          </span>
          <button
            onClick={() => void handleLogout()}
            className="flex items-center gap-1.5 rounded-md border border-slate-700 px-2.5 py-1 text-xs font-medium text-slate-200 transition-colors hover:bg-slate-800"
          >
            <LogOut size={11} />
            {t("auth.logout")}
          </button>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        <aside className="hidden w-60 flex-col bg-slate-950 md:flex">
          <nav className="flex-1 space-y-0.5 overflow-y-auto px-2 py-3">
            {ADMIN_NAV_ITEMS.map((item) => {
              const isActive = location.pathname.startsWith(item.to);
              const Icon = item.icon;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={cn(
                    "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
                    isActive
                      ? "bg-purple-500/15 text-purple-100"
                      : "text-slate-400 hover:bg-slate-900 hover:text-slate-200",
                  )}
                >
                  <Icon
                    size={15}
                    className={cn("shrink-0", isActive ? "text-purple-400" : "text-current")}
                  />
                  <span className="flex-1 truncate">{t(item.labelKey)}</span>
                </Link>
              );
            })}
          </nav>

          <div className="border-t border-purple-900/30 px-4 py-3">
            <p className="text-[10px] uppercase tracking-wider text-purple-400/40">
              {t("admin_layout.footer_label")}
            </p>
          </div>
        </aside>

        <main className="flex-1 overflow-auto bg-background p-5 md:p-6">
          {isImpersonating && (
            <div className="mb-5 flex items-start gap-3 rounded-md border border-purple-300 bg-purple-50 px-4 py-3 text-sm dark:border-purple-700 dark:bg-purple-950/40">
              <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-purple-600 dark:text-purple-400" />
              <div className="flex-1">
                <div className="font-medium text-purple-900 dark:text-purple-100">
                  {t("impersonation_banner.title")}
                </div>
                <div className="mt-0.5 text-xs text-purple-800 dark:text-purple-200">
                  {t("impersonation_banner.hint", { tenant_id: claims?.tenant_id ?? "?" })}
                </div>
              </div>
              <button
                type="button"
                disabled={exitImpersonationMut.isPending}
                onClick={() => exitImpersonationMut.mutate()}
                className="ml-2 shrink-0 rounded border border-purple-400 bg-white px-2.5 py-1 text-xs font-medium text-purple-900 hover:bg-purple-50 disabled:opacity-50 dark:border-purple-700 dark:bg-purple-900/40 dark:text-purple-100"
              >
                {t("impersonation_banner.exit")}
              </button>
            </div>
          )}
          <ErrorBoundary key={location.pathname}>
            <Suspense fallback={<SkeletonText lines={6} className="max-w-xl" />}>
              <Outlet />
            </Suspense>
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
}
